import api from './api';

export interface InformacionCaja {
  codigoCaja: number;
  nombre: string;
  fechaApertura: string;
  fechaCierre: string | null;
  estado: string;
  usuarioResponsable: string;
  saldoInicial: number;
  totalIngresos: number;
  totalEgresos: number;
  saldoFinal: number;
  cantidadMovimientosTotal?: number;
  cantidadMovimientosIngresos?: number;
  cantidadMovimientosEgresos?: number;
}

export interface MovimientoCaja {
  nroFormulario: number;
  nroTipoFormulario: string;
  nombreTipoFormulario: string;
  fechaEmision: string;
  usuario: string;
  entidad: number;
  nombre: string;
  detalle: string;
  importe: number;
  tipoMovimiento: string;
  mcp: string;
  nombreMCP: string;
  tipoPago: string;
  cartera: number;
  nombreCartera: string;
  letra?: string;
  nroSuc?: number;
  nroComprobante?: number;
  codigoCajaOrigen?: number | null;
  codigoCajaDestino?: number | null;
  nombreCajaOrigen?: string | null;
  nombreCajaDestino?: string | null;
  nombreBancoCheque?: string | null;
  nroBancoCheque?: number | null;
  nroSucursalCheque?: number | null;
  nroCheque?: number | null;
  nroLibrador?: string | null;
}

export function formatNumeroChequeMovimiento(
  mov: Pick<MovimientoCaja, 'nroBancoCheque' | 'nroSucursalCheque' | 'nroCheque'>
): string {
  const { nroBancoCheque, nroSucursalCheque, nroCheque } = mov;
  if (nroBancoCheque != null && nroSucursalCheque != null && nroCheque != null) {
    return `${nroBancoCheque}-${nroSucursalCheque}-${nroCheque}`;
  }
  if (nroCheque != null) return String(nroCheque);
  return '-';
}

export function movimientoTieneDatosCheque(
  mov: Pick<MovimientoCaja, 'tipoPago' | 'nombreBancoCheque' | 'nroCheque' | 'nroLibrador'>
): boolean {
  const tipo = (mov.tipoPago ?? '').trim().toUpperCase();
  if (tipo.startsWith('C') || tipo.startsWith('P')) return true;
  return Boolean(
    (mov.nombreBancoCheque ?? '').trim() ||
    mov.nroCheque != null ||
    (mov.nroLibrador ?? '').trim()
  );
}

export function esMovimientoTraspaso(mov: Pick<MovimientoCaja, 'nroTipoFormulario'>): boolean {
  return String(mov.nroTipoFormulario ?? '').trim() === '17';
}

export function formatCajaPlanillaLabel(codigo?: number | null, nombre?: string | null): string {
  if (codigo == null || codigo <= 0) return '-';
  const nom = (nombre ?? '').trim();
  return nom ? `${codigo} — ${nom}` : String(codigo);
}

export interface DetalleCajaResponse {
  items: MovimientoCaja[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface CajaResumen {
  codigoCaja: number;
  descripcion: string;
  estado?: string;
  grupo?: number;
  fechaApertura?: string | null;
  fechaCierre?: string | null;
  grupoNombre?: string | null;
}

export interface ArqueoPorTipoPago {
  tipoPago: string;
  descripcion?: string;
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface ArqueoPorMcp {
  mcp: string;
  nombreMcp: string;
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface ArqueoPorCartera {
  cartera: number;
  nombreCartera: string;
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface ArqueoPorTipoFormulario {
  tipoFormulario: string;
  nombreTipoFormulario: string;
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface ArqueoPorUsuario {
  usuario: string;
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface ArqueoPorEntidad {
  entidad: number;
  nombre: string;
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface ArqueoPorFecha {
  fechaEmision: string;
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface InformacionArqueo {
  codigoCaja: number;
  nombreCaja: string | null;
  porTipoPago: ArqueoPorTipoPago[];
  porMcp: ArqueoPorMcp[];
  porCartera: ArqueoPorCartera[];
  porTipoFormulario?: ArqueoPorTipoFormulario[];
  porUsuario?: ArqueoPorUsuario[];
  porEntidad?: ArqueoPorEntidad[];
  porFecha?: ArqueoPorFecha[];
  totalIngresos: number;
  totalEgresos: number;
  saldoFinal: number;
}

export interface CajaSearchParams {
  pageNumber: number;
  pageSize: number;
  searchTerm?: string;
  estado?: string;
  grupo?: number;
  fechaAperturaDesde?: string;
  fechaAperturaHasta?: string;
  fechaCierreDesde?: string;
  fechaCierreHasta?: string;
  /** Si es true, filtra solo cajas de grupos asignados al usuario. Por defecto false (todas las cajas). */
  soloCajasDelUsuario?: boolean;
}

export interface CajaSearchResponse {
  items: CajaResumen[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface CierreCajaRequest {
  codigoCaja: number;
  grupoDestino?: number;
  nroSuc?: number;
}

export interface CierreCajaResponse {
  exito: boolean;
  codigoCaja: number;
  estado: string;
  saldoCaja: number;
  codigoCajaNueva?: number;
  nrosFormularioTransferencia?: number[];
  mensaje?: string;
}

export interface CajaCreateRequest {
  descripcion: string;
  grupo?: number;
  estado?: string;
  usrLubri?: string;
  fechaApertura?: string;
}

export interface CajaCreateResponse {
  codigoCaja: number;
  descripcion: string;
  estado: string;
  grupo?: number;
  usrLubri?: string;
  fechaApertura?: string;
  [key: string]: any;
}

/** PDF del movimiento según tipo de formulario (misma impresión que en pantallas dedicadas). */
export function getMovimientoCajaPdfConfig(
  nroFormulario: number,
  nroTipoFormulario?: string
): { url: string; title: string; defaultFileName: string } {
  const tipo = (nroTipoFormulario ?? '').trim();
  switch (tipo) {
    case '4':
      return {
        url: `/api/tesoreria/recibos/${nroFormulario}/pdf`,
        title: `Recibo ${nroFormulario}`,
        defaultFileName: `recibo_${nroFormulario}.pdf`,
      };
    case '6':
      return {
        url: `/api/Tesoreria/ordendepago/${nroFormulario}/pdf`,
        title: `Orden de pago ${nroFormulario}`,
        defaultFileName: `orden-pago_${nroFormulario}.pdf`,
      };
    case '17':
      return {
        url: `/api/TesoreriaCaja/transferencias-caja/${nroFormulario}/pdf`,
        title: `Traspaso ${nroFormulario}`,
        defaultFileName: `traspaso-caja_${nroFormulario}.pdf`,
      };
    default:
      return {
        url: `/api/TesoreriaCaja/movimientos-caja/${nroFormulario}/pdf`,
        title: `Movimiento de caja ${nroFormulario}`,
        defaultFileName: `movimiento-caja_${nroFormulario}.pdf`,
      };
  }
}

const tesoreriaCajaService = {
  /**
   * Obtiene la información general y resumen de una caja
   * @param codigoCaja - Código de la caja
   * @returns Información de la caja incluyendo saldos calculados
   */
  getInformacionCaja: async (codigoCaja: number): Promise<InformacionCaja> => {
    const response = await api.get<InformacionCaja>(`/api/Caja/${codigoCaja}/informacion`);
    return response.data;
  },

  /**
   * Obtiene los movimientos detallados de una caja (una página específica)
   * @param codigoCaja - Código de la caja
   * @param pageNumber - Número de página (opcional, por defecto 1)
   * @param pageSize - Tamaño de página (opcional, por defecto 50)
   * @param tipoMovimiento - Filtro por tipo: Ingreso o Egreso (opcional)
   * @param fechaDesde - Fecha desde en formato YYYY-MM-DD (opcional)
   * @param fechaHasta - Fecha hasta en formato YYYY-MM-DD (opcional)
   * @param tipoPago - Código exacto de tipo de pago (E, T, TB, C, etc.) (opcional)
   * @returns Lista paginada de movimientos de la caja
   */
  getDetalleCaja: async (
    codigoCaja: number,
    pageNumber: number = 1,
    pageSize: number = 50,
    tipoMovimiento?: 'Ingreso' | 'Egreso',
    fechaDesde?: string,
    fechaHasta?: string,
    tipoPago?: string,
    nroSuc?: number
  ): Promise<DetalleCajaResponse> => {
    const params: Record<string, unknown> = {
      PageNumber: pageNumber,
      PageSize: pageSize,
    };

    if (tipoMovimiento) {
      params.TipoMovimiento = tipoMovimiento;
    }
    if (fechaDesde) {
      params.FechaDesde = fechaDesde;
    }
    if (fechaHasta) {
      params.FechaHasta = fechaHasta;
    }
    if (tipoPago?.trim()) {
      params.TipoPago = tipoPago.trim();
    }
    if (nroSuc != null && nroSuc > 0) {
      params.NroSuc = nroSuc;
    }

    const response = await api.get<DetalleCajaResponse>(`/api/Caja/${codigoCaja}/informaciondetalle`, {
      params,
    });
    return response.data;
  },

  /**
   * Verifica si el usuario actual puede operar la caja (misma regla que cierre-caja y listado con SoloCajasDelUsuario).
   */
  tieneAccesoACaja: async (codigoCaja: number): Promise<boolean> => {
    try {
      const response = await tesoreriaCajaService.searchCajas({
        pageNumber: 1,
        pageSize: 10,
        searchTerm: String(codigoCaja),
        soloCajasDelUsuario: true,
      });
      return response.items.some((c) => c.codigoCaja === codigoCaja);
    } catch {
      return false;
    }
  },

  /**
   * Busca cajas utilizando el endpoint paginado /api/Caja
   */
  searchCajas: async (params: CajaSearchParams): Promise<CajaSearchResponse> => {
    const response = await api.get<any>('/api/Caja', {
      params: {
        PageNumber: params.pageNumber,
        PageSize: params.pageSize,
        SearchTerm: params.searchTerm || undefined,
        Estado: params.estado || undefined,
        Grupo: params.grupo || undefined,
        FechaAperturaDesde: params.fechaAperturaDesde || undefined,
        FechaAperturaHasta: params.fechaAperturaHasta || undefined,
        FechaCierreDesde: params.fechaCierreDesde || undefined,
        FechaCierreHasta: params.fechaCierreHasta || undefined,
        SoloCajasDelUsuario: params.soloCajasDelUsuario ?? false,
      },
    });

    const data = response.data;
    const rawItems: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : [];

    const items: CajaResumen[] = rawItems
      .map((it: any): CajaResumen => {
        const codigoRaw =
          it?.codigoCaja ?? it?.nroCaja ?? it?.codigo ?? it?.id ?? 0;
        const codigoNum = Number(codigoRaw) || 0;

        const descRaw = (it?.descripcion ?? it?.nombre) as string | undefined;
        const descTrim = descRaw && String(descRaw).trim();
        const descripcion =
          descTrim && descTrim.length > 0 ? descTrim : `Caja ${codigoNum}`;

        return {
          codigoCaja: codigoNum,
          descripcion,
          estado: it?.estado,
          grupo: it?.grupo ?? it?.grupoMcp ?? undefined,
          fechaApertura: it?.fechaApertura ?? null,
          fechaCierre: it?.fechaCierre ?? null,
          grupoNombre: it?.grupoNombre ?? null,
        };
      })
      .filter((c) => c.codigoCaja > 0);

    return {
      items,
      totalCount: data?.totalCount ?? items.length,
      pageNumber: data?.pageNumber ?? params.pageNumber,
      pageSize: data?.pageSize ?? params.pageSize,
      totalPages: data?.totalPages ?? 1,
    };
  },

  /**
   * Obtiene la información del arqueo de una caja
   * @param codigoCaja - Código de la caja
   * @returns Información del arqueo agrupada por tipo de pago, MCP y cartera
   */
  getInformacionArqueo: async (codigoCaja: number): Promise<InformacionArqueo> => {
    const response = await api.get<InformacionArqueo>(`/api/Caja/${codigoCaja}/informacionarqueo`);
    return response.data;
  },

  /**
   * Cierra una caja. Si saldo > 0, requiere grupoDestino y nroSuc para transferir.
   */
  cierreCaja: async (request: CierreCajaRequest): Promise<CierreCajaResponse> => {
    const payload = {
      codigoCaja: request.codigoCaja,
      grupoDestino: request.grupoDestino ?? null,
      nroSuc: request.nroSuc ?? null,
    };
    const response = await api.post<CierreCajaResponse>('/api/TesoreriaCaja/cierre-caja', payload);
    const d = response.data as any;
    return {
      exito: d.exito ?? d.Exito ?? false,
      codigoCaja: d.codigoCaja ?? d.CodigoCaja ?? 0,
      estado: d.estado ?? d.Estado ?? '',
      saldoCaja: d.saldoCaja ?? d.SaldoCaja ?? 0,
      codigoCajaNueva: d.codigoCajaNueva ?? d.CodigoCajaNueva,
      nrosFormularioTransferencia: d.nrosFormularioTransferencia ?? d.NrosFormularioTransferencia ?? [],
      mensaje: d.mensaje ?? d.Mensaje,
    };
  },

  /**
   * Descarga el PDF del informe de cierre de caja.
   * Predeterminado (sin parámetro): columnas por tipo de pago. Use `ingresoEgreso` para el listado clásico.
   */
  getCierrePdf: async (
    codigoCaja: number,
    formato?: 'ingresoEgreso' | 'porTipoPago'
  ): Promise<Blob> => {
    const response = await api.get(`/api/Caja/${codigoCaja}/cierre/pdf`, {
      responseType: 'blob',
      ...(formato === 'ingresoEgreso' ? { params: { formato: 'ingresoEgreso' } } : {}),
    });
    return response.data;
  },

  /**
   * Crea una nueva caja (abre una caja).
   * POST /api/Caja
   */
  createCaja: async (request: CajaCreateRequest): Promise<CajaCreateResponse> => {
    const response = await api.post<CajaCreateResponse>('/api/Caja', request);
    return response.data;
  },
};

export default tesoreriaCajaService;
